import React, { useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';

interface Option {
  [key: string]: any;
}

interface SearchableDropdownProps {
  options: any[];
  labelKey?: string; // Key to use for the label
  valueKey?: string; // Key to use for the value
  label?: any;
  value: any | any[] | null;
  onChange: (value: any | any[] | null) => void;
  loading?: boolean;
  onSearch?: (searchText: string) => void;
  sx?: object;
  multiSelect?: boolean;
  error?: boolean;
  errorMessage?: string;
  fetchMoreOptions?: (searchText: string) => void;
  disabled?: boolean;
  getOptionDisabled?: (option: any) => boolean;
  getOptionLabel?: (option: any) => any;
  searchValue?: string;
  size?: 'small' | 'medium';
  className?: string;
  onOpen?: () => void;
  onClose?: () => void;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options = [],
  labelKey = 'label',
  valueKey = 'value',
  label = 'Select an Option',
  value,
  onChange,
  onOpen,
  onClose,
  loading = false,
  onSearch,
  sx = {},
  multiSelect = false,
  error = false,
  errorMessage = 'Invalid',
  fetchMoreOptions,
  disabled = false,
  getOptionDisabled,
  getOptionLabel,
  searchValue = "",
  size = 'medium',
  className = "",
}) => {
  const [inputValue, setInputValue] = useState<string>('');

  const handleInputChange = (
    event: React.SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    if (reason === 'input') {
      setInputValue(newValue);
      if (onSearch) {
        onSearch(newValue);
      }
    }
  };

  // const handleOptionSelect = (
  //   event: React.SyntheticEvent,
  //   selectedValue: Option | Option[] | null
  // ) => {
  //   if (onChange) {
  //     onChange(selectedValue);
  //   }
  // };



  const handleOptionSelect = (
    event: React.SyntheticEvent,
    selectedValue: Option | Option[] | null
  ) => {
    if (onChange) {
      onChange(selectedValue);
    }
    if (multiSelect) {
      if (!selectedValue || selectedValue.length === 0) {
        setInputValue('');
        if (onSearch) {
          onSearch('');
        }
      }
    } else {
      setInputValue('');
      if (onSearch) {
        onSearch('');
      }
    }
  };

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget as HTMLElement;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight) {
      if (fetchMoreOptions) {
        fetchMoreOptions(inputValue); // Load more options based on the current input
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }} className={className || ''}>
      <Autocomplete
        fullWidth
        size={size}
        multiple={multiSelect}
        onOpen={() => {
          onOpen?.();
        }}
        onClose={() => {
          onClose?.();
        }}
        options={
          multiSelect
            ? options.filter(
              (option) =>
                !value ||
                !Array.isArray(value) ||
                !value.some(
                  (selected) => selected[valueKey] === option[valueKey]
                )
            )
            : options
        }
        getOptionLabel={(option) =>
          getOptionLabel
            ? getOptionLabel(option)
            : option?.[labelKey] || option?.[valueKey] || ''
        }
        value={value || (multiSelect ? [] : null)}
        onInputChange={handleInputChange}
        onChange={handleOptionSelect}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            size="small"
            error={error}
            value={inputValue || searchValue || ''}
            helperText={error ? errorMessage : null}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{ ...sx, width: '100%' }}
          />
        )}
        ListboxProps={{
          onScroll: handleScroll,
        }}
        loading={loading}
        sx={{ ...sx, width: '100%' }}
        disabled={disabled}
        getOptionDisabled={getOptionDisabled}

      />
    </Box>
  );
};

export default SearchableDropdown;
